import React from "react";
import { createPortal } from "react-dom";
import { Printer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { DAYS } from "@/components/UserForm";
import Ean13 from "@/components/Ean13";
import { QRCodeSVG } from "qrcode.react";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-2 py-1 border-b border-border text-sm">
      <span className="text-muted-foreground">{label}</span><span className="font-medium text-foreground">{value || "—"}</span>
    </div>
  );
}

export default function UserSheet({ user, onClose }) {
  const { t } = useLang();
  React.useEffect(() => {
    document.body.classList.add("print-user");
    return () => document.body.classList.remove("print-user");
  }, []);
  const slots = (d) => (user.workdays?.[d] || []).filter((s) => s.start || s.end).map((s) => `${s.start || "?"}–${s.end || "?"}`).join("  ·  ") || "—";

  return createPortal(
    <div id="user-print-sheet" className="fixed inset-0 z-[60] bg-slate-900/40 flex items-start justify-center overflow-auto p-6 print:p-0 print:bg-card print:static" data-testid="user-sheet">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl p-8 print:shadow-none print:rounded-none print:max-w-none">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("users")}</p>
            <h2 className="font-display font-extrabold text-2xl text-foreground">{user.nickname || user.first_name} <span className="text-primary">#{user.number}</span></h2>
            <p className="text-sm text-muted-foreground">{user.first_name} {user.last_name} · {t(`role_${user.role}`)} · {user.active ? t("active") : t("inactive")}</p>
          </div>
          <div className="flex items-start gap-4">
            <div className="hidden sm:block print:block text-center">
              <Ean13 code={user.number} />
              <div className="mt-2 flex justify-center"><QRCodeSVG value={user.number} size={74} level="M" /></div>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("u_badge")}</p>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button onClick={() => window.print()} data-testid="user-sheet-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
              <Button variant="outline" size="icon" onClick={onClose} data-testid="user-sheet-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <Row label={t("email")} value={user.email} />
            <Row label={t("u_staff_number")} value={user.staff_number} />
            <Row label={t("u_min_week_hours")} value={user.min_week_hours ? `${user.min_week_hours}u` : ""} />
            <Row label={t("phone")} value={user.phone} />
            <Row label={t("u_birth")} value={fmtDate(user.birth_date)} />
            <Row label={t("language")} value={(user.language || "").toUpperCase()} />
          </div>
          <div>
            <Row label={t("u_street")} value={[user.street, user.house_number].filter(Boolean).join(" ")} />
            <Row label={t("u_postal")} value={user.postal_code} />
            <Row label={t("u_city")} value={user.city} />
            <Row label={t("created")} value={fmtDate(user.created_at)} />
          </div>
        </div>
        <h3 className="font-semibold text-sm text-foreground mt-6 mb-2">{t("u_workdays")}</h3>
        <div className="rounded-lg border border-border overflow-hidden text-sm">
          {DAYS.map((d) => (
            <div key={d} className="grid grid-cols-[60px_1fr] px-3 py-1.5 border-b border-border last:border-0">
              <span className="font-semibold text-foreground/90">{t(`day_${d}`)}</span><span className="text-foreground/90 tabular-nums">{slots(d)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
